import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useUiStore, useBranding, i18n } from '@bms/common';
import { foregroundForPrimary, type BrandingConfig } from '@/themes/branding';
import { FLAVOUR_PRESETS, DEFAULT_PRESET, type BrandPreset } from '@/themes/defaults';

// ============================================================================
// Theme Context
// ============================================================================

export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** The mode actually painted right now. */
  mode: 'light' | 'dark';
  /** What the user chose — 'system' is a preference, never a painted mode. */
  preference: ThemePreference;
  branding: BrandingConfig;
  preset: BrandPreset;
  spaceCategory: string;
  /** Cycles light → dark → system → light. */
  toggleMode: () => void;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';
const BUNDLED_FONT_FAMILIES = new Set(['inter', 'manrope', 'geist mono']);

// Fallbacks for the theme-color meta when --background cannot be resolved to a
// paintable colour (jsdom, or a browser that will not parse oklch in canvas).
// They match --background in index.css.
const FALLBACK_BACKGROUND: Record<'light' | 'dark', string> = {
  light: '#f3f3f3',
  dark: '#0a0a0a',
};

/**
 * Normalise any CSS colour — including the oklch() the tokens are written in —
 * to a hex string the theme-color meta is guaranteed to accept. Canvas is the
 * cheapest colour parser the platform exposes; if it is unavailable or refuses
 * the value, the caller's fallback is used.
 */
function toPaintableColor(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#000000';
    ctx.fillStyle = raw;
    const parsed = ctx.fillStyle;
    // fillStyle stays '#000000' both when the value really is black and when
    // parsing failed; treating black as unparsed is safe here because no
    // --background value is pure black.
    return typeof parsed === 'string' && parsed !== '#000000' ? parsed : null;
  } catch {
    return null;
  }
}

// ============================================================================
// ThemeProvider
// ============================================================================

function readSystemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;
}

function subscribeSystemDark(onChange: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const uiTheme = useUiStore((s) => s.userTheme);
  const setTheme = useUiStore((s) => s.setUserTheme);

  // Fetch branding config from backend (public, no auth)
  const { data: brandingData } = useBranding();

  const spaceCategory = brandingData?.spaceCategory || 'residential';
  const storedBranding = brandingData?.branding;
  const branding: BrandingConfig = useMemo(() => {
    if (!storedBranding) return {};
    return { ...storedBranding };
  }, [storedBranding]);

  // Resolve preset from space_category
  const preset = useMemo(
    () => FLAVOUR_PRESETS[spaceCategory] || DEFAULT_PRESET,
    [spaceCategory]
  );

  // Track the OS preference so 'system' follows it live, not only on reload.
  const systemPrefersDark = useSyncExternalStore(subscribeSystemDark, readSystemDark, () => false);

  // The login screen is always dark; the stored preference is untouched.
  const forceDark = useLocation().pathname === '/login';

  // Resolve effective mode
  const effectiveMode: 'light' | 'dark' = useMemo(() => {
    if (forceDark) return 'dark';
    if (uiTheme === 'system') {
      if (systemPrefersDark) return 'dark';
      // Explicit per-site override wins over the space-category preset default
      const darkDefault = branding?.darkDefault ?? preset.darkDefault;
      return darkDefault ? 'dark' : 'light';
    }
    return uiTheme;
  }, [forceDark, uiTheme, systemPrefersDark, branding?.darkDefault, preset.darkDefault]);

  // Apply dark class
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [effectiveMode]);

  // `lang` follows the active i18n locale rather than the `en` baked into
  // index.html, so a Croatian session is announced correctly by assistive
  // tech and hyphenation/quoting picks the right language.
  useEffect(() => {
    const applyLang = () => {
      document.documentElement.lang = i18n.language || 'en';
    };
    applyLang();
    i18n.on('languageChanged', applyLang);
    return () => {
      i18n.off('languageChanged', applyLang);
    };
  }, []);

  // Point the browser chrome at the surface the app actually paints. Two
  // theme-color metas ship in index.html, one per prefers-color-scheme; the one
  // matching the resolved mode is rewritten from --background so an explicit
  // light/dark choice that disagrees with the OS still gets the right chrome.
  useEffect(() => {
    const resolved =
      toPaintableColor(
        getComputedStyle(document.documentElement).getPropertyValue('--background')
      ) ?? FALLBACK_BACKGROUND[effectiveMode];

    const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
    const wanted = effectiveMode === 'dark' ? DARK_QUERY : '(prefers-color-scheme: light)';
    metas.forEach((meta) => {
      const media = meta.getAttribute('media');
      if (!media || media === wanted) meta.setAttribute('content', resolved);
    });
  }, [effectiveMode, branding]);

  // Apply CSS custom properties from explicit branding and preset typography.
  // Default colors come from the stylesheet (index.css oklch values), so only
  // override colors when branding explicitly provides values.
  useEffect(() => {
    const root = document.documentElement;
    const applied: string[] = [];

    // Primary color — only from explicit branding config. --ring stays bound to
    // --primary-text / --primary in the stylesheet so it keeps its per-mode
    // step; a raw brand hex is not readable as a focus ring in light mode.
    if (branding?.primaryColor) {
      root.style.setProperty('--primary', branding.primaryColor);
      applied.push('--primary');

      const foreground = foregroundForPrimary(branding.primaryColor);
      if (foreground) {
        root.style.setProperty('--primary-foreground', foreground);
        applied.push('--primary-foreground');
      }
    }

    // Accent color — a brand accent of its own. It is deliberately NOT written
    // into --accent: that token is shadcn's hover/active surface and must stay
    // achromatic, or every hover in the app turns into the brand colour.
    if (branding?.accentColor) {
      root.style.setProperty('--brand-accent', branding.accentColor);
      applied.push('--brand-accent');
    }

    // Font family — from branding or preset (safe default is Inter)
    const fontFamily = branding?.fontFamily || preset.fontFamily;
    if (fontFamily) {
      root.style.setProperty('--font-family', `"${fontFamily}"`);
      applied.push('--font-family');
    }

    root.style.setProperty('--font-display-family', `"${preset.displayFontFamily}"`);
    applied.push('--font-display-family');

    root.style.setProperty('--font-mono-family', `"${preset.monoFontFamily}"`);
    applied.push('--font-mono-family');

    return () => {
      // Cleanup custom properties on unmount
      applied.forEach((prop) => root.style.removeProperty(prop));
    };
  }, [branding, preset]);

  // Load a custom UI font only when this build does not bundle it locally.
  useEffect(() => {
    const fontFamily = branding?.fontFamily || preset.fontFamily;
    const isBundled = fontFamily
      ? BUNDLED_FONT_FAMILIES.has(fontFamily.trim().toLowerCase())
      : false;
    if (fontFamily && !isBundled) {
      // This Google path fails silently on an isolated site network and exists
      // only for a branding family this build does not ship.
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [branding?.fontFamily, preset.fontFamily]);

  const setPreference = useCallback(
    (preference: ThemePreference) => setTheme(preference),
    [setTheme]
  );

  // light → dark → system → light. 'system' is a real, selectable state: the
  // store defaults to it, so most users start on whatever their OS says.
  const toggleMode = useCallback(() => {
    setTheme(uiTheme === 'light' ? 'dark' : uiTheme === 'dark' ? 'system' : 'light');
  }, [setTheme, uiTheme]);

  const value: ThemeContextValue = useMemo(
    () => ({
      mode: effectiveMode,
      preference: uiTheme,
      branding: branding || {},
      preset,
      spaceCategory,
      toggleMode,
      setPreference,
    }),
    [effectiveMode, uiTheme, branding, preset, spaceCategory, toggleMode, setPreference]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
